import { Badge, Group, NumberFormatter, Stack, Text } from '@mantine/core'
import { IconCalendarEvent } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import type { RedeemableUnderlyingRedemption } from '@repo/common/queries/admin/redeem'

import { TowerCard } from '@/components/ui/TowerCard'

import styles from './UnderlyingRedemptionsTable.module.css'

interface Props {
  underlyingRedemptions: RedeemableUnderlyingRedemption[]
  totalCount: number
  slim?: boolean
  redeemableId?: string
}

function getSlimRows(
  rows: RedeemableUnderlyingRedemption[],
): RedeemableUnderlyingRedemption[] {
  const now = Date.now()
  const past = rows
    .filter((r) => new Date(r.redemption_at).getTime() <= now)
    .sort(
      (a, b) =>
        new Date(b.redemption_at).getTime() -
        new Date(a.redemption_at).getTime(),
    )
  const upcoming = rows
    .filter((r) => new Date(r.redemption_at).getTime() > now)
    .sort(
      (a, b) =>
        new Date(a.redemption_at).getTime() -
        new Date(b.redemption_at).getTime(),
    )
  const result: RedeemableUnderlyingRedemption[] = []
  if (past[0]) result.push(past[0])
  if (upcoming[0]) result.push(upcoming[0])
  return result
}

function isMostRecentPast(
  r: RedeemableUnderlyingRedemption,
  allRows: RedeemableUnderlyingRedemption[],
): boolean {
  const now = Date.now()
  const pastRows = allRows
    .filter((row) => new Date(row.redemption_at).getTime() <= now)
    .sort(
      (a, b) =>
        new Date(b.redemption_at).getTime() -
        new Date(a.redemption_at).getTime(),
    )
  return pastRows[0]?.id === r.id
}

function isSoonestUpcoming(
  r: RedeemableUnderlyingRedemption,
  allRows: RedeemableUnderlyingRedemption[],
): boolean {
  const now = Date.now()
  const upcomingRows = allRows
    .filter((row) => new Date(row.redemption_at).getTime() > now)
    .sort(
      (a, b) =>
        new Date(a.redemption_at).getTime() -
        new Date(b.redemption_at).getTime(),
    )
  return upcomingRows[0]?.id === r.id
}

export const UnderlyingRedemptionsTable: React.FC<Props> = ({
  underlyingRedemptions,
  totalCount,
  slim = false,
  redeemableId,
}) => {
  const displayRows = slim
    ? getSlimRows(underlyingRedemptions)
    : underlyingRedemptions

  return (
    <TowerCard
      title="Underlying Redemption Schedule"
      icon={<IconCalendarEvent size={24} />}
    >
      <Stack gap="xs">
        {displayRows.length === 0 ? (
          <Text ta="center" c="dimmed" py="lg">
            No underlying redemptions found
          </Text>
        ) : (
          <>
            <div className={styles.table_wrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Redemption At</th>
                    <th>Days Until</th>
                    <th className={styles.cell_right}>Total AUM</th>
                    <th>Created</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((r: RedeemableUnderlyingRedemption) => {
                    const isPast = isMostRecentPast(r, underlyingRedemptions)
                    const isUpcoming = isSoonestUpcoming(
                      r,
                      underlyingRedemptions,
                    )
                    return (
                      <tr key={r.id}>
                        <td>
                          <Text size="xs" ff="monospace">
                            {r.id.slice(0, 8)}...
                          </Text>
                        </td>
                        <td>
                          <Text
                            size="xs"
                            className={
                              isUpcoming ? styles.cell_active : undefined
                            }
                          >
                            {dayjs(r.redemption_at).format(
                              'MMM D, YYYY h:mm A',
                            )}
                          </Text>
                        </td>
                        <td>
                          {r.days_until != null ? (
                            <Badge
                              size="xs"
                              variant={isUpcoming ? 'filled' : 'light'}
                              color={
                                isUpcoming
                                  ? 'green'
                                  : r.days_until <= 0
                                    ? 'green'
                                    : 'blue'
                              }
                            >
                              {r.days_until <= 0
                                ? 'Past'
                                : `${Math.ceil(r.days_until)}d`}
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </td>
                        <td className={styles.cell_right}>
                          {r.total_aum != null ? (
                            <Text
                              size="xs"
                              component="span"
                              className={
                                isPast ? styles.cell_active : undefined
                              }
                            >
                              <NumberFormatter
                                value={r.total_aum}
                                thousandSeparator
                                decimalScale={2}
                              />
                            </Text>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </td>
                        <td>
                          <Text size="xs">
                            {dayjs(r.created_at).format('MMM D, YYYY h:mm A')}
                          </Text>
                        </td>
                        <td>
                          <Text size="xs">
                            {dayjs(r.updated_at).format('MMM D, YYYY h:mm A')}
                          </Text>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <Group className={slim ? styles.footer : undefined}>
              {slim && redeemableId ? (
                <Link to="/redeem/$id/underlying" params={{ id: redeemableId }}>
                  {totalCount} underlying redemptions
                </Link>
              ) : (
                <Text size="sm" c="dimmed">
                  Showing {displayRows.length} of {totalCount} underlying
                  redemptions
                </Text>
              )}
            </Group>
          </>
        )}
      </Stack>
    </TowerCard>
  )
}
